import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useMemo } from "react";
import {
  Upload,
  Download,
  PackageCheck,
  Search,
  User as UserIcon,
  Users,
  ShieldCheck,
  BookUser,
  LogOut,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import { toImageDataUrl } from "@/lib/image";
import OfflineIndicator from "@/components/OfflineIndicator";
import { can } from "@/lib/permissions";

const tabs = [
  { to: "/delivrance", label: "Délivrance", icon: PackageCheck, testid: "tab-delivrance", action: "delivrance" },
  { to: "/restitution", label: "Restitution", icon: Download, testid: "tab-restitution", action: "restitution" },
  { to: "/depot", label: "Dépôt", icon: Upload, testid: "tab-depot", action: "depot" },
  { to: "/recherche", label: "Recherche", icon: Search, testid: "tab-recherche", action: "search.global" },
  { to: "/profil", label: "Profil", icon: UserIcon, testid: "tab-profil", action: null }, // always allowed
];

// Extra tabs only shown in desktop sidebar
const sidebarExtraTabs = [
  { to: "/clients", label: "Clients", icon: Users, testid: "tab-clients", action: "client.list" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const branding = useBranding();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  // Memoise the per-user filtered tab lists. Without useMemo, every Layout
  // render — and there are many of those because the navigation triggers a
  // re-render of the parent — re-evaluates `can(user, action)` for every
  // tab. Memoising keys on the (stable) user reference so the lists are
  // computed only when the user changes.
  const visibleSidebarTabs = useMemo(
    () => tabs.filter((t) => t.to !== "/profil" && (!t.action || can(user, t.action))),
    [user],
  );
  const visibleSidebarExtraTabs = useMemo(
    () => sidebarExtraTabs.filter((t) => !t.action || can(user, t.action)),
    [user],
  );
  const visibleBottomTabs = useMemo(
    () => tabs.filter((t) => !t.action || can(user, t.action)),
    [user],
  );

  return (
    <div className="min-h-screen flex bg-[var(--vidoc-bg)]">
      {/* Sidebar — desktop only */}
      <aside className="hidden md:flex md:w-64 lg:w-72 border-r border-[var(--vidoc-border)] bg-white flex-col">
        <div className="px-6 py-6 border-b border-[var(--vidoc-border)]">
          <div className="flex items-center gap-3">
            {branding.icon_base64 ? (
              <img
                src={toImageDataUrl(branding.icon_base64)}
                alt=""
                className="w-20 h-20 rounded-xl object-contain bg-white"
              />
            ) : (
              <div className="w-20 h-20 rounded-xl bg-[var(--vidoc-primary)] flex items-center justify-center text-white font-bold text-2xl">
                V
              </div>
            )}
            <div>
              <div className="font-semibold text-[var(--vidoc-text)]">
                {branding.company_name || "VIDOC"}
              </div>
              {branding.company_tagline && (
                <div className="text-xs text-[var(--vidoc-text-secondary)]">
                  {branding.company_tagline}
                </div>
              )}
            </div>
          </div>
          <div className="mt-3 flex justify-start">
            <OfflineIndicator />
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {visibleSidebarTabs.map((t) => (
            <SidebarLink key={t.to} {...t} />
          ))}
          {visibleSidebarExtraTabs.map((t) => (
            <SidebarLink key={t.to} {...t} />
          ))}

          {user?.role === "admin" && (
            <>
              <div className="pt-4 pb-2 px-3 text-xs uppercase tracking-wider text-[var(--vidoc-text-tertiary)]">
                Administration
              </div>
              <SidebarLink to="/admin/users" label="Utilisateurs" icon={Users} testid="tab-admin-users" />
              <SidebarLink to="/admin/icons" label="Logo & marque" icon={ShieldCheck} testid="tab-admin-icons" />
              <SidebarLink to="/admin/backup-emails" label="Emails sauvegarde" icon={Search} testid="tab-admin-backup" />
              <SidebarLink to="/admin/contacts" label="Carnet d'adresses" icon={BookUser} testid="tab-admin-contacts" />
              <SidebarLink to="/admin/purge" label="Purge & nettoyage" icon={ShieldCheck} testid="tab-admin-purge" />
            </>
          )}
        </nav>

        <div className="px-3 py-4 border-t border-[var(--vidoc-border)]">
          <NavLink
            to="/profil"
            data-testid="tab-profil"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-3 rounded-xl touch-target ${
                isActive
                  ? "bg-[var(--vidoc-bg-secondary)]"
                  : "hover:bg-[var(--vidoc-bg-secondary)]"
              }`
            }
          >
            <UserIcon className="w-5 h-5 text-[var(--vidoc-text-secondary)]" />
            <div className="flex-1">
              <div className="text-sm font-medium">{user?.username}</div>
              <div className="text-xs text-[var(--vidoc-text-secondary)]">
                {(() => {
                  if (user?.role === "admin") return "Administrateur";
                  if (user?.role === "expert") return "Expert";
                  if (user?.role === "responsable" || user?.role === "user") return "Responsable";
                  return "Utilisateur";
                })()}
              </div>
            </div>
          </NavLink>
          <button
            data-testid="sidebar-logout-btn"
            onClick={handleLogout}
            className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-sm text-[var(--vidoc-danger)] hover:bg-red-50 touch-target"
          >
            <LogOut className="w-4 h-4" />
            Déconnexion
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar — mobile */}
        <header className="md:hidden flex items-center justify-between px-4 py-3 border-b border-[var(--vidoc-border)] bg-white sticky top-0 z-30">
          <div className="flex items-center gap-3">
            {branding.icon_base64 ? (
              <img
                src={toImageDataUrl(branding.icon_base64)}
                alt=""
                className="w-16 h-16 rounded-xl object-contain bg-white"
              />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-[var(--vidoc-primary)] flex items-center justify-center text-white font-bold text-xl">
                V
              </div>
            )}
            <span className="font-semibold text-lg">
              {branding.company_name || "VIDOC"}
            </span>
          </div>
          <OfflineIndicator />
        </header>

        <main className="flex-1 pb-24 md:pb-8 overflow-y-auto">
          <Outlet />
        </main>

        {/* Bottom tabs — mobile only */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[var(--vidoc-border)] z-40">
          <div className="flex justify-around items-stretch pb-[env(safe-area-inset-bottom)]">
            {visibleBottomTabs.map((t) => (
                <NavLink
                  key={t.to}
                  to={t.to}
                  data-testid={`bottom-${t.testid}`}
                  className={({ isActive }) =>
                    `flex-1 flex flex-col items-center justify-center gap-1 py-2 touch-target ${
                      isActive
                        ? "text-[var(--vidoc-primary)]"
                        : "text-[var(--vidoc-text-secondary)]"
                    }`
                  }
                >
                  <t.icon className="w-5 h-5" />
                  <span className="text-[11px] font-medium">{t.label}</span>
                </NavLink>
              ))}
          </div>
        </nav>
      </div>
    </div>
  );
}

function SidebarLink({ to, label, icon: Icon, testid }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium touch-target ${
          isActive
            ? "bg-[var(--vidoc-primary)] text-white"
            : "text-[var(--vidoc-text)] hover:bg-[var(--vidoc-bg-secondary)]"
        }`
      }
    >
      <Icon className="w-5 h-5" />
      {label}
    </NavLink>
  );
}
